import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns/format';

import { Bank } from '../../../types/payment-config.type';
import { useRemoteConfigStore } from '../../../stores/remote-config-store';
import { ValidationErrors } from '../../../utils/validate-schema';
import styles from '../../payment-form/payment-form.module.scss';
import Input from '../../input/input';

export type MobilePaymentData = {
    bankId: number;
    isDirectDebitMode: boolean;
    date?: string;
    referenceCode?: string;
    isThirdPartyPayer: boolean;
    phone?: string;
    nin?: string;
    name?: string;
    documentType?: 'V' | 'E';
};

type Props = {
    banks: Bank[];
    onData: (data: MobilePaymentData) => void;
    errors?: ValidationErrors;
    initialValues?: Partial<MobilePaymentData>;
};

const MobilePaymentForm = ({ banks, onData, errors, initialValues }: Props) => {
    const [bankId, setBankId] = useState(initialValues?.bankId ?? 0);
    const [date, setDate] = useState(initialValues?.date ?? '');
    const [referenceCode, setReferenceCode] = useState(
        initialValues?.referenceCode ?? ''
    );
    const [isThirdPartyPayer, setIsThirdPartyPayer] = useState(
        initialValues?.isThirdPartyPayer ?? false
    );
    const [phone, setPhone] = useState(initialValues?.phone ?? '');
    const [nin, setNin] = useState(initialValues?.nin ?? '');
    const [name, setName] = useState(initialValues?.name ?? '');
    const [documentType, setDocumentType] = useState<'V' | 'E' | ''>(
        initialValues?.documentType ?? ''
    );

    const remoteConfig = useRemoteConfigStore((s) => s.config);
    const directDebitBankCodes = remoteConfig?.directDebit?.bankCodes ?? [];
    const selectedBank = banks.find((b) => b.id === bankId);
    const isDirectDebitBank =
        !!selectedBank && directDebitBankCodes.includes(selectedBank.code);
    const isDirectDebitMode = bankId === 0 || isDirectDebitBank;

    useEffect(() => {
        onData({
            bankId,
            isDirectDebitMode,
            isThirdPartyPayer: isDirectDebitMode ? isThirdPartyPayer : false,
            date: isDirectDebitMode ? undefined : date,
            referenceCode: isDirectDebitMode
                ? undefined
                : referenceCode || undefined,
            phone: isDirectDebitMode && isThirdPartyPayer ? phone : undefined,
            nin: isDirectDebitMode && isThirdPartyPayer ? nin : undefined,
            name: isDirectDebitMode && isThirdPartyPayer ? name : undefined,
            documentType:
                isDirectDebitMode && isThirdPartyPayer
                    ? documentType || undefined
                    : undefined,
        });
    }, [
        bankId,
        isDirectDebitMode,
        date,
        referenceCode,
        isThirdPartyPayer,
        phone,
        nin,
        name,
        documentType,
    ]);

    const selectBank = useCallback((ev: ChangeEvent<HTMLSelectElement>) => {
        setBankId(Number(ev.target.value));
    }, []);

    const selectDate = (strDate: string) => {
        const date = format(
            new Date(strDate.replaceAll('-', '/')),
            'dd/MM/yyyy'
        );
        setDate(date);
    };

    const getError = (field: string) => errors?.get(field);

    return (
        <form className={styles.paymentForm}>
            <div className={styles.info}>
                <span>Utilizando Pago Móvil:</span>
                <b>Banco Venezolano de Crédito</b>
                <b>J-410713747</b>
                <b>0424-2479718</b>
            </div>

            <select
                className={styles.selectField}
                value={bankId}
                onChange={selectBank}
            >
                <option value="" disabled>
                    — Seleccione su Banco —
                </option>
                {banks.map((bank) => (
                    <option key={bank.code} value={bank.id}>
                        ({bank.code}) {bank.name}
                    </option>
                ))}
            </select>
            {getError('bankId') && (
                <span className={styles.error}>{getError('bankId')}</span>
            )}

            {isDirectDebitMode && (
                <>
                    <label className={styles.toggleLabel}>
                        <input
                            type="checkbox"
                            checked={isThirdPartyPayer}
                            onChange={(e) =>
                                setIsThirdPartyPayer(e.target.checked)
                            }
                            data-testid="third-party-toggle"
                        />
                        El pago lo realiza un tercero
                    </label>

                    {isThirdPartyPayer && (
                        <>
                            <div className={styles.documentRow}>
                                <select
                                    className={styles.selectFieldInline}
                                    value={documentType}
                                    onChange={(e) =>
                                        setDocumentType(
                                            e.target.value as 'V' | 'E'
                                        )
                                    }
                                    data-testid="document-type-select"
                                >
                                    <option value="" disabled>
                                        Tipo
                                    </option>
                                    <option value="V">V</option>
                                    <option value="E">E</option>
                                </select>
                                <Input
                                    placeholder={'Cédula del pagador'}
                                    onInput={setNin}
                                    value={nin}
                                    maxLength={12}
                                    error={getError('nin')}
                                    data-testid="payer-nin"
                                />
                            </div>
                            {getError('documentType') && (
                                <span className={styles.error}>
                                    {getError('documentType')}
                                </span>
                            )}

                            <Input
                                placeholder={'Teléfono del pagador'}
                                onInput={setPhone}
                                value={phone}
                                type="tel"
                                maxLength={15}
                                error={getError('phone')}
                                data-testid="payer-phone"
                            />

                            <Input
                                placeholder={'Nombre del pagador'}
                                onInput={setName}
                                value={name}
                                error={getError('name')}
                                data-testid="payer-name"
                            />
                        </>
                    )}
                </>
            )}

            {!isDirectDebitMode && (
                <>
                    <Input
                        placeholder={'Fecha del pago'}
                        onInput={selectDate}
                        type="date"
                        error={getError('date')}
                        data-testid="date-input"
                    />

                    <Input
                        placeholder={'Número de referencia (opcional)'}
                        onInput={setReferenceCode}
                        value={referenceCode}
                        error={getError('referenceCode')}
                        data-testid="reference-input"
                    />
                </>
            )}
        </form>
    );
};

export { MobilePaymentForm };
